/* Входы «Фотосессии» ↔ вид формы движка шаблона (план вехи 3, шаг 2 экрана; план shoot §2.4–2.5). Чистые функции без DOM и запросов:
   - shootLists: документ экрана (GET /app/studio/screens/shoot) и форма сервера → списки в виде шаблона (LOOKS, AR, WISH, SHOT_NAMES)
     и устройство фотосессии (conf шаблона: название, выбор образов, образы по умолчанию, люди, действия); вид фотосессии — по той же цепочке,
     что у родной формы (shoot-model.ts shootScreen: поле shoot_id, ограничения шаблона, единственное значение поля, вход экрана), затем
     default_shoot документа;
   - readShoot / writeShoot: входы ↔ вид (образы, свой референс, формат, пожелания, люди) — только опубликованными ключами и тем же
     кодированием, что родная форма (shoot/kit/form-model.ts encode; «Вы» — JSON {src, avatar_id}), поэтому заявки обеих форм читаются друг
     другом (I1, откат). Ключи своего референса (reference…) и «Съёмки» (shoot) пишут их мосты (ref-bridge, cam-bridge). */
import type { StudioForm } from '../studio/studio-api';
import { artworkUrl } from '../studio/studio-art-model';
import { record, rows, text } from '../studio/studio-content';
import { peopleSel, type SelfRef, type SelItem } from '../studio/engine-kit/people-bridge';
import { decode, encode, fieldOf, limitOf, listOf, unitPrice, valueOf, type Inputs } from './kit/form-model';
import { chosenLooks, shootKeys, shootScreen, type ShootScreen } from './shoot-model';
import { PROMPT_MAX } from '../studio/prompt-limit';

/** Кадр образа в виде шаблона (shots LOOKS): картинка — адрес сервера (url) */
export interface EngineShot {
  n: string;
  ch: null;
  pos: null;
  z: number;
  url: string;
}
/** Образ в виде шаблона (LOOKS): id — строка сервера, ch/f — пусто (демо-портретов и цветокоррекции нет), url — обложка образа */
export interface EngineLook {
  id: string;
  n: string;
  d: string;
  ch: null;
  f: string;
  th: string[];
  pop: number;
  isNew: boolean;
  hit: boolean;
  url: string;
  shots: EngineShot[];
}
/** Устройство фотосессии в виде шаблона (conf(): SHOOTS[…] или апка) */
export interface ShootConf {
  title: string;
  pick: boolean;
  /** образы по умолчанию у общей (default_look_ids) */
  def: string[];
  /** состав готовой фотосессии (всё входит) */
  looks: string[];
  max: number;
  roles: string[];
  acts: string[];
  own: boolean;
}
export interface ShootLists {
  /** Модель родной формы (shoot-model.ts) */
  screen: ShootScreen;
  kind: string;
  conf: ShootConf;
  looks: EngineLook[];
  themes: string[];
  /** AR шаблона: [пропорции, слово, пояснение] */
  formats: string[][];
  /** WISH шаблона: [подпись, полный текст] */
  wishes: string[][];
  shotNames: string[];
  wishMax: number;
  ref: { name: string; shots: number; shield: string };
  /** C за кадр (null — сервер не публикует) */
  unit: number | null;
  unitWord: string;
}
/** Вид формы: то, что из входов ложится в S движка */
export interface ShootView {
  /** выбранные образы каталога (у готовой — её состав) */
  looks: string[];
  /** свой референс входит в фотосессию ('ref' в look_ids) */
  ref: boolean;
  ratio: string;
  wishes: string;
  /** люди ряда «Кто в кадре»: близкие (id) с действием и персонажи ('c:<id>') */
  sel: SelItem[];
}

/** Формат словами, если документ его не называет (родная форма: FORMAT_NAMES, FORMAT_SUBS) */
const FORMAT_NAMES: Record<string, string> = { '3:4': 'Портрет', '4:5': 'Пост', '9:16': 'Сторис', '1:1': 'Квадрат' };
const FORMAT_SUBS: Record<string, string> = { '3:4': 'Для портрета', '4:5': 'Для ленты соцсетей', '9:16': 'На весь экран телефона', '1:1': 'Для аватарки и соцсетей' };
const ROLES = ['adult', 'kid', 'old', 'pet'];
const strings = (value: unknown) => (Array.isArray(value) ? value.map(item => (typeof item === 'number' ? String(item) : item)).filter((item): item is string => typeof item === 'string' && !!item) : []);
const positive = (value: unknown, fallback: number) => (typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : fallback);
const image = (value: string | null | undefined) => (value ? (artworkUrl(value) ?? '') : '');

/** Документ экрана, форма и входы → списки шаблона. entry — вход экрана (app.entry и начальные входы кадра), app — название приложения */
export function shootLists(doc: Record<string, unknown>, form: StudioForm | null, inputs: Inputs, entry: Record<string, unknown>, app: string): ShootLists {
  const defShoot = text(entry.shoot) || text(doc.default_shoot);
  const screen = shootScreen(doc, form, inputs, { ...entry, ...(defShoot ? { shoot: defShoot } : {}) });
  const kind = screen.kind.id;
  const raw = record(rows(doc.shoots).find(item => text(item.id) === kind));
  const constraints = record(record(form?.constraints).shoot);
  const names = strings(doc.shot_names);
  const perLook = screen.shotsPerLook;
  const looks: EngineLook[] = screen.looks.map(look => {
    /* образ без своих кадров входит кадрами по умолчанию (shots_per_look): цена и мозаика — как у родной формы (frames) */
    const shots = look.shots.length
      ? look.shots.map((shot, i) => ({ n: shot.name || names[i % Math.max(1, names.length)] || '', ch: null, pos: null, z: 1, url: image(shot.image) }))
      : Array.from({ length: perLook }, (_, i) => ({ n: names[i % Math.max(1, names.length)] ?? '', ch: null, pos: null, z: 1, url: '' }));
    return { id: look.id, n: look.name, d: look.description, ch: null, f: '', th: look.themes.slice(), pop: look.popularity, isNew: look.isNew, hit: look.hit, url: image(look.cover), shots };
  });
  const known = new Set(looks.map(look => look.id));
  const def = strings(raw.default_look_ids ?? constraints.default_look_ids).filter(id => known.has(id));
  const ratio = fieldOf(form, shootKeys(form).ratio ?? 'ratio');
  const listed = screen.formats.map(item => [item.id, item.name || FORMAT_NAMES[item.id] || item.id, item.sub || FORMAT_SUBS[item.id] || '']);
  const formats = ratio?.values.length
    ? ratio.values.map(value => listed.find(item => item[0] === value) ?? [value, FORMAT_NAMES[value] ?? value, FORMAT_SUBS[value] ?? ''])
    : listed;
  const chips = rows(doc.wish_chips).flatMap(chip => (text(chip.label) ? [[text(chip.label), text(chip.text) || text(chip.label)]] : []));
  const reference = record(doc.reference);
  const wishKey = shootKeys(form).wishes;
  const price = record(doc.price);
  return {
    screen,
    kind,
    conf: {
      title: (kind === 'app' ? app : text(raw.name)) || app,
      pick: screen.kind.pick,
      def,
      looks: screen.kind.pick ? [] : (screen.kind.looks.length ? screen.kind.looks : looks.map(look => look.id)).filter(id => known.has(id)),
      max: screen.kind.maxPeople,
      roles: strings(raw.roles).length ? strings(raw.roles) : ROLES,
      acts: screen.kind.actions,
      own: raw.own_action !== false,
    },
    looks,
    themes: screen.themes.length ? screen.themes : [...new Set(looks.flatMap(look => look.th))],
    formats,
    wishes: chips.length ? chips : screen.wishes.map(chip => [chip, chip]),
    shotNames: names,
    wishMax: limitOf(wishKey ? fieldOf(form, wishKey) : undefined, Math.max(positive(doc.wishes_max, 0), PROMPT_MAX)),
    ref: { name: text(reference.name), shots: positive(reference.shots, perLook), shield: text(reference.shield) },
    unit: unitPrice(form, doc, 'shoot'),
    unitWord: text(price.unit_text) || 'за кадр',
  };
}

/** Входы → вид. Образы у общей — из ключа образов (без 'ref'), у готовой — её состав; формат — значение поля или первый формат */
export function readShoot(form: StudioForm | null, lists: ShootLists, inputs: Inputs): ShootView {
  const keys = shootKeys(form);
  const known = new Set(lists.looks.map(look => look.id));
  const looks = lists.conf.pick ? chosenLooks(lists.screen, inputs, keys.looks).filter(id => known.has(id)) : lists.conf.looks.slice();
  const people = peopleSel(
    {
      persons: keys.persons ? decode<unknown[]>(inputs[keys.persons], []) : [],
      chars: keys.chars ? decode<unknown[]>(inputs[keys.chars], []) : [],
    },
    lists.conf.acts,
  );
  return {
    looks,
    ref: !!keys.looks && listOf(inputs[keys.looks]).includes('ref'),
    ratio: valueOf(form, inputs, keys.ratio) || lists.formats[0]?.[0] || '3:4',
    wishes: valueOf(form, inputs, keys.wishes),
    sel: people.sel.map(item => {
      const { id, act, own } = item;
      return { id, ...(act !== undefined ? { act } : {}), ...(own !== undefined ? { own } : {}) };
    }),
  };
}

/** Ключ образов задан во входах (пусть и пустым списком нет: пустой список родная форма удаляет) */
export const hasLooks = (form: StudioForm | null, inputs: Inputs) => {
  const key = shootKeys(form).looks;
  return !!key && Object.hasOwn(inputs, key);
};

/** Значение поля по правилам родной формы (writeField): пустое — ключа нет */
function put(next: Inputs, form: StudioForm | null, key: string | undefined, value: unknown) {
  if (!key) return;
  const encoded = encode(fieldOf(form, key), value);
  if (encoded === undefined || encoded === '' || encoded === '[]') delete next[key];
  else next[key] = encoded;
}

/** Вид → входы поверх prev: только опубликованные ключи. me — «Вы» в заказ (selfRef: только когда «Вы» готов); persons/chars — люди ряда
    (people-bridge peopleOf); shoot — inputs.shoot блока «Съёмка» (undefined — ключ не трогать, null — убрать) */
export function writeShoot(
  form: StudioForm | null,
  lists: ShootLists,
  v: ShootView,
  prev: Inputs,
  ctx: { me: SelfRef | null; persons: Array<{ person_id: number; action?: string }>; chars: Array<{ avatar_id: number }>; shoot?: string | null },
): Inputs {
  const keys = shootKeys(form);
  const next = { ...prev };
  /* вид фотосессии и состав готовой — производные экрана (родная форма: fixedInputs, без удаления) */
  const kind = keys.shootId ? encode(fieldOf(form, keys.shootId), lists.kind) : undefined;
  if (keys.shootId && kind !== undefined) next[keys.shootId] = kind;
  if (lists.conf.pick) put(next, form, keys.looks, v.ref ? [...v.looks, 'ref'] : v.looks);
  else if (keys.looks && lists.screen.kind.looks.length) {
    const fixed = encode(fieldOf(form, keys.looks), lists.screen.kind.looks);
    if (fixed !== undefined) next[keys.looks] = fixed;
  }
  /* «Вы» — тем, что сейчас стоит за «Вы» в аккаунте (родная форма: JSON без кодирования поля) */
  if (keys.me) {
    if (ctx.me) next[keys.me] = JSON.stringify(ctx.me);
    else delete next[keys.me];
  }
  put(next, form, keys.persons, ctx.persons);
  put(next, form, keys.chars, ctx.chars);
  if (keys.ratio && v.ratio) put(next, form, keys.ratio, v.ratio);
  put(next, form, keys.wishes, v.wishes);
  if (keys.shoot && ctx.shoot !== undefined) {
    if (ctx.shoot === null || ctx.shoot === '{}') delete next[keys.shoot];
    else next[keys.shoot] = ctx.shoot;
  }
  return next;
}

/** Форма публикует то, без чего экран на движке не работает: «Вы» (обязательный объект me) и образы или вид фотосессии */
export function shootSupported(form: StudioForm | null): boolean {
  const keys = shootKeys(form);
  return !!keys.me && (!!keys.looks || !!keys.shootId);
}
